import "./style.css";
import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { CloseIcon } from "../../icons";
import { cn } from "../../../utils/classnames";

/**
 * A growable list of inputs (e.g. "Additional Email Recipients").
 * Rows fade/scale in when added and out when removed, the remaining rows
 * glide into their new places, and the whole block eases to its new height
 * so the form below moves smoothly instead of jumping.
 */

const EASE_OUT = [0.22, 1, 0.36, 1];

let nextRowId = 0;
const createRowId = () => `row-${nextRowId++}`;

// The parent only stores values (["a@b.com", ""]). Keys must not be the array
// index, or removing row 1 would animate row 2 away instead. So each row gets
// an id that is kept in step with the values: new ids when rows are added,
// the removed row's id when one is removed.
const useStableRowIds = (rowCount) => {
    const [rowIds, setRowIds] = useState(() => Array.from({ length: rowCount }, createRowId));

    if (rowIds.length < rowCount) {
        setRowIds([...rowIds, ...Array.from({ length: rowCount - rowIds.length }, createRowId)]);
    } else if (rowIds.length > rowCount) {
        // Shrunk from outside (e.g. the form was reset): drop ids from the end.
        setRowIds(rowIds.slice(0, rowCount));
    }

    const removeRowId = (index) => setRowIds((ids) => ids.filter((_, idIndex) => idIndex !== index));
    return [rowIds, removeRowId];
};

// Height of an element, kept up to date as its content changes. The observer
// is created and torn down in one effect, so it survives React re-running
// effects (StrictMode, hot reload) instead of being left disconnected.
const useMeasuredHeight = () => {
    const [height, setHeight] = useState("auto");
    const [element, setElement] = useState(null);

    useEffect(() => {
        if (!element) return undefined;
        const observer = new ResizeObserver(() => setHeight(element.offsetHeight));
        observer.observe(element);
        return () => observer.disconnect();
    }, [element]);

    return [setElement, height];
};

const rowMotion = (shouldReduce) => ({
    initial: { opacity: 0, scale: shouldReduce ? 1 : 0.96, y: shouldReduce ? 0 : 8 },
    animate: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.3, ease: EASE_OUT } },
    exit: { opacity: 0, scale: shouldReduce ? 1 : 0.96, transition: { duration: 0.18 } },
});

const RemoveRowButton = ({ onClick, label }) => (
    <motion.button
        type="button"
        onClick={onClick}
        aria-label={label}
        className="tw:mr-2 tw:flex tw:h-8 tw:w-8 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:transition-colors tw:hover:bg-[#F9EDD3]"
        whileTap={{ scale: 0.9 }}
    >
        <CloseIcon size={10} color="#8D8D8D" />
    </motion.button>
);

const MultipleInput = ({
    type = "text",
    valueArray,
    removeIndex,
    arrayError = [],
    prefixIcon = null,
    placeholder = "",
    suffixClass = "tw:!mb-3",
    mbLabel = "tw:!mb-1",
    suffixClassLabel,
    onChange,
    name = "",
    label = null,
    disable = false,
    onBlur,
    id,
}) => {
    const shouldReduce = useReducedMotion();
    const [rowIds, removeRowId] = useStableRowIds(valueArray.length);
    const [measureRef, listHeight] = useMeasuredHeight();
    const canRemoveRows = valueArray.length > 1;

    // Put the cursor in a row the moment it is added.
    const previousRowCount = useRef(valueArray.length);
    useEffect(() => {
        if (valueArray.length > previousRowCount.current) {
            document.getElementById(`${id}${valueArray.length - 1}`)?.focus();
        }
        previousRowCount.current = valueArray.length;
    }, [valueArray.length, id]);

    const handleRemove = (index) => {
        removeRowId(index);
        removeIndex(index);
    };

    const getRowError = (index) => arrayError.find((error) => error.index === index)?.message;

    return (
        <div className={suffixClass}>
            {label && <p className={`${mbLabel} ${suffixClassLabel}`}>{label}</p>}

            {/* Eases to the list's new height. overflow-hidden is needed while the
                height animates, so the measured list carries a little padding
                (offset by the negative margin) to keep the gold focus ring visible. */}
            <motion.div
                className="tw:-mx-1 tw:-mt-1 tw:overflow-hidden"
                animate={{ height: listHeight }}
                transition={shouldReduce ? { duration: 0 } : { duration: 0.3, ease: EASE_OUT }}
            >
                <div ref={measureRef} className="tw:relative tw:!flex tw:!flex-wrap tw:!gap-3 tw:px-1 tw:pt-1 tw:pb-3">
                    <AnimatePresence initial={false} mode="popLayout">
                        {valueArray.map((value, index) => (
                            <motion.div
                                key={rowIds[index] ?? index}
                                layout={!shouldReduce}
                                {...rowMotion(shouldReduce)}
                                className="tw:!w-full tw:md:!w-[calc(50%-0.375rem)]"
                            >
                                <div
                                    className={cn(
                                        "tw:flex tw:items-center tw:gap-2 tw:rounded tw:border tw:bg-white tw:transition-[border-color,box-shadow] tw:duration-200",
                                        "tw:focus-within:border-[#DBBE78] tw:focus-within:shadow-[0_0_0_3px_rgba(219,190,120,0.18)]",
                                        getRowError(index) ? "tw:border-[#E5484D]" : "tw:border-[#DEE2E6]"
                                    )}
                                >
                                    {prefixIcon && <span className="tw:!ml-3 tw:flex">{prefixIcon}</span>}
                                    <input
                                        className={cn(
                                            "input-field tw:!w-full tw:!rounded tw:!border-0 tw:!py-2 no-calendar",
                                            !prefixIcon && "tw:!px-3",
                                            disable && "bg-grey-light"
                                        )}
                                        type={type}
                                        id={`${id}${index}`}
                                        placeholder={placeholder}
                                        onChange={(event) => onChange(index, event)}
                                        name={name}
                                        value={value}
                                        disabled={disable}
                                        onBlur={onBlur}
                                    />
                                    {canRemoveRows && (
                                        <RemoveRowButton onClick={() => handleRemove(index)} label={`Remove ${placeholder} ${index + 1}`} />
                                    )}
                                </div>

                                {getRowError(index) && <p className="tw:!m-0 tw:!text-red-500">{getRowError(index)}</p>}
                            </motion.div>
                        ))}
                    </AnimatePresence>
                </div>
            </motion.div>
        </div>
    );
};

export default MultipleInput;
